import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, shuffle, useGame, useRoundTimer, type GameProps } from '../sdk/react';
import { labelInstruction } from '../phrases';
import { Chip, DropSpot, usePlacer } from '../kit/place';
import { PartMini, SceneView, type Placed } from '../scenes/SceneView';
import { sceneOf, type ScenePart } from '../scenes/scenes';
import { KINDS } from '../../concepts/kinds';
import { Pic } from '../sdk/react';

type Board = { targets: ScenePart[]; cards: ScenePart[]; trials: Record<string, string> };

export default function LabelParts(props: GameProps) {
  const game = useGame(props);
  const scene = sceneOf(game.config?.scene);
  const wanted = Array.isArray(game.config?.parts) ? (game.config!.parts as string[]) : [];
  const targetsAll = scene ? scene.parts.filter((p) => wanted.includes(p.id)) : [];
  const [board, setBoard] = useState<Board | null>(null);
  const [placed, setPlaced] = useState<Record<string, string>>({});
  const [bounce, setBounce] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const busy = useRef(false);
  const face = game.str('chipFace', 'both');
  const speakCards = game.bool('chipSpeech', true);
  const dots = game.str('spotHint', 'dots') === 'dots';
  const extra = game.num('extraCards', 0);
  const instruction = labelInstruction(scene?.kind);

  const start = useCallback(async () => {
    if (!scene || targetsAll.length === 0) return;
    setDone(false);
    setPlaced({});
    const others = shuffle(scene.parts.filter((p) => !wanted.includes(p.id))).slice(0, extra);
    const trials: Record<string, string> = {};
    for (const t of targetsAll) {
      const id = await game.register({ scene: scene.id, part: t.id, cards: [...targetsAll, ...others].map((p) => p.id) }, { value: t.id });
      if (!id) return;
      trials[t.id] = id;
    }
    setBoard({ targets: targetsAll, cards: shuffle([...targetsAll, ...others]), trials });
  }, [scene?.id, wanted.join(), extra]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => { if (board) game.say(instruction); }, [board]);

  const placer = usePlacer((cardId, spotId) => { void drop(cardId, spotId); });

  async function drop(cardId: string, spotId: string) {
    if (!board || busy.current || placed[spotId] || !board.trials[spotId]) return;
    if (game.errorless && cardId !== spotId) { setBounce(cardId); window.setTimeout(() => setBounce(null), 400); return; }
    busy.current = true;
    const verdict = await game.respond(board.trials[spotId], cardId);
    busy.current = false;
    if (!verdict) return;
    if (verdict.correct) {
      const next = { ...placed, [spotId]: cardId };
      setPlaced(next);
      if (board.targets.every((t) => next[t.id])) setDone(true);
      return;
    }
    setBounce(cardId);
    window.setTimeout(() => setBounce(null), 400);
    const fresh = await game.register({ scene: scene!.id, part: spotId, retry: true }, { value: spotId });
    if (fresh) setBoard((b) => (b ? { ...b, trials: { ...b.trials, [spotId]: fresh } } : b));
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); }, 1200);

  const shown: Record<string, Placed> = {};
  if (scene) {
    for (const [spot, card] of Object.entries(placed)) {
      const part = scene.parts.find((p) => p.id === card);
      if (part?.wear) shown[spot] = { emoji: part.wear };
    }
  }

  const firstTrial = board ? Object.values(board.trials)[0] ?? null : null;

  return (
    <GameFrame
      game={game}
      empty={!scene || targetsAll.length === 0}
      emptyText={`${KINDS.labelParts.describes} Edit it to choose a picture and its parts.`}
      onResume={() => { void start(); }}
      breakTrial={firstTrial}
    >
      {scene && board && (
        <div className={`g-board g-label${fading ? ' is-fading' : ''}`}>
          <PromptLine game={game} text={instruction} />
          <SceneView
            scene={scene}
            placed={shown}
            overlay={(part, left, top) => {
              if (!board.trials[part.id]) return null;
              const filled = placed[part.id];
              const card = filled ? scene.parts.find((p) => p.id === filled) : null;
              if (filled && card?.wear) return null;
              if (filled) {
                return <span key={part.id} className="g-tag" style={{ left, top }}>{card?.label}</span>;
              }
              const target = placer.held && game.errorless && placer.held === part.id;
              return (
                <DropSpot
                  key={part.id}
                  placer={placer}
                  id={part.id}
                  label={`Spot ${board.targets.findIndex((t) => t.id === part.id) + 1}`}
                  className={`${dots ? 'is-marked' : 'is-hidden'}${target ? ' is-target' : ''}`}
                  style={{ left, top }}
                  inert={game.errorless && !!placer.held && placer.held !== part.id}
                />
              );
            }}
          />
          <div className="g-tray" role="group" aria-label="Cards">
            {board.cards.filter((c) => !Object.values(placed).includes(c.id)).map((c) => (
              <Chip
                key={c.id}
                placer={placer}
                id={c.id}
                label={c.label}
                className={`${bounce === c.id ? 'is-bounce' : ''}${face === 'word' ? ' is-word' : ''}`}
                onTap={() => { if (speakCards) game.say(c.label); }}
              >
                {face !== 'word' && <Pic text={c.label} fallback={<PartMini scene={scene} part={c} />} className="g-chip-pic" />}
                {face !== 'picture' && <span className="g-chip-word">{c.label}</span>}
              </Chip>
            ))}
          </div>
        </div>
      )}
    </GameFrame>
  );
}
